import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Buildings, 
  Phone, 
  MapPin, 
  CreditCard, 
  CloudArrowUp, 
  Trash, 
  FloppyDisk, 
  Image, 
  Shield, 
  Percent, 
  MapPinArea, 
  Envelope,
  CheckCircle,
  WarningCircle,
  Info,
  Briefcase,
  FileText,
  Leaf,
  ArrowSquareOut,
  Gear,
  Target,
  CurrencyEur
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API } from '@/App';

const Settings = () => {
  const [settings, setSettings] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    phone_country_code: '+33',
    company_siret: '',
    company_tva_number: '',
    company_garantie_decennale: '',
    company_status: 'micro',
    company_location: 'idf',
    job_domain: 'general',
    deposit_percentage: 30,
    smtp_email: '',
    smtp_password: '',
    smtp_server: 'smtp.gmail.com',
    smtp_port: 587,
    // Extended fields for Chorus Pro
    company_ape_code: '',
    company_rcs: '',
    company_capital: '',
    company_assurance_name: '',
    company_assurance_number: '',
    company_rge_number: '',
    company_rge_domains: [],
    // Chorus Pro
    chorus_pro_enabled: false,
    chorus_pro_tech_id: '',
    chorus_pro_structure_id: '',
    chorus_pro_service_code: '',
    // Monthly objective
    monthly_objective: 10000
  });
  const [statuses, setStatuses] = useState({});
  const [locations, setLocations] = useState({});
  const [jobDomains, setJobDomains] = useState({});
  const [rulesPreview, setRulesPreview] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [activeTab, setActiveTab] = useState('general');  // general, chorus, rge

  // Country codes for phone (France, DOM-TOM, Francophonie, Anglophones)
  const countryCodes = [
    // France métropolitaine
    { code: '+33', country: 'France', flag: '🇫🇷' },
    // DOM-TOM
    { code: '+590', country: 'Guadeloupe', flag: '🇬🇵' },
    { code: '+594', country: 'Guyane', flag: '🇬🇫' },
    { code: '+596', country: 'Martinique', flag: '🇲🇶' },
    { code: '+262', country: 'Réunion', flag: '🇷🇪' },
    { code: '+269', country: 'Mayotte', flag: '🇾🇹' },
    { code: '+508', country: 'Saint-Pierre-et-Miquelon', flag: '🇵🇲' },
    { code: '+681', country: 'Wallis-et-Futuna', flag: '🇼🇫' },
    { code: '+689', country: 'Polynésie française', flag: '🇵🇫' },
    { code: '+687', country: 'Nouvelle-Calédonie', flag: '🇳🇨' },
    // Pays francophones
    { code: '+32', country: 'Belgique', flag: '🇧🇪' },
    { code: '+41', country: 'Suisse', flag: '🇨🇭' },
    { code: '+352', country: 'Luxembourg', flag: '🇱🇺' },
    { code: '+377', country: 'Monaco', flag: '🇲🇨' },
    { code: '+1', country: 'Canada', flag: '🇨🇦' },
    // Pays anglophones
    { code: '+44', country: 'Royaume-Uni', flag: '🇬🇧' },
    { code: '+1', country: 'États-Unis', flag: '🇺🇸' },
    { code: '+61', country: 'Australie', flag: '🇦🇺' },
    { code: '+64', country: 'Nouvelle-Zélande', flag: '🇳🇿' },
    { code: '+353', country: 'Irlande', flag: '🇮🇪' },
    { code: '+27', country: 'Afrique du Sud', flag: '🇿🇦' },
    // Autres pays européens
    { code: '+49', country: 'Allemagne', flag: '🇩🇪' },
    { code: '+34', country: 'Espagne', flag: '🇪🇸' },
    { code: '+39', country: 'Italie', flag: '🇮🇹' },
    { code: '+351', country: 'Portugal', flag: '🇵🇹' },
    { code: '+31', country: 'Pays-Bas', flag: '🇳🇱' },
  ];

  // Validation functions
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateSiret = (siret) => {
    const cleaned = siret.replace(/\s/g, '');
    return /^\d{14}$/.test(cleaned);
  };

  const validateTVA = (tva) => {
    if (!tva) return true;
    const cleaned = tva.replace(/\s/g, '');
    return /^FR\d{11}$/.test(cleaned);
  };

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\s/g, '');
    return /^\d{9,10}$/.test(cleaned);
  };

  const formatSiret = (value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 14);
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, '$1 $2 $3 $4').trim();
  };

  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 10);
    return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5').trim();
  };

  useEffect(() => {
    fetchConfig();
    fetchSettings();
  }, []);

  useEffect(() => {
    // Fetch rules preview when status or location changes
    if (settings.company_status && settings.company_location) {
      fetchRulesPreview();
    }
  }, [settings.company_status, settings.company_location]);

  const fetchConfig = async () => {
    try {
      const [statusesRes, locationsRes, domainsRes] = await Promise.all([
        axios.get(`${API}/config/company-statuses`),
        axios.get(`${API}/config/locations`),
        axios.get(`${API}/config/job-domains`)
      ]);
      setStatuses(statusesRes.data);
      setLocations(locationsRes.data);
      setJobDomains(domainsRes.data);
    } catch (error) {
      console.error('Error loading config');
    }
  };

  const fetchRulesPreview = async () => {
    try {
      const res = await axios.get(`${API}/config/rules?company_status=${settings.company_status}&location=${settings.company_location}`);
      setRulesPreview(res.data);
    } catch (error) {
      console.error('Error loading rules preview');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      const phone = response.data.company_phone || '';
      const phoneCode = countryCodes.find(c => phone.startsWith(c.code))?.code || '+33';
      const phoneNumber = phone.replace(phoneCode, '').trim();
      
      setSettings({
        company_name: response.data.company_name || '',
        company_address: response.data.company_address || '',
        company_phone: phoneNumber,
        phone_country_code: response.data.phone_country_code || phoneCode,
        company_siret: response.data.company_siret || '',
        company_tva_number: response.data.company_tva_number || '',
        company_garantie_decennale: response.data.company_garantie_decennale || '',
        company_status: response.data.company_status || 'micro',
        company_location: response.data.company_location || 'idf',
        job_domain: response.data.job_domain || 'general',
        deposit_percentage: response.data.deposit_percentage || 30,
        smtp_email: response.data.smtp_email || '',
        smtp_password: '',
        smtp_server: response.data.smtp_server || 'smtp.gmail.com',
        smtp_port: response.data.smtp_port || 587
      });
      setHasLogo(!!response.data.company_logo_base64);
      setSmtpConfigured(response.data.smtp_configured || false);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate before saving
    const errors = {};
    
    if (settings.smtp_email && !validateEmail(settings.smtp_email)) {
      errors.smtp_email = 'Format email invalide';
    }
    
    if (settings.company_siret && !validateSiret(settings.company_siret)) {
      errors.company_siret = 'Le SIRET doit contenir 14 chiffres';
    }
    
    if (settings.company_tva_number && !validateTVA(settings.company_tva_number)) {
      errors.company_tva_number = 'Format TVA invalide (ex: FR12345678901)';
    }
    
    if (settings.company_phone && !validatePhone(settings.company_phone)) {
      errors.company_phone = 'Numéro de téléphone invalide';
    }
    
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      toast.error('Veuillez corriger les erreurs de validation');
      return;
    }
    
    setSaving(true);
    try {
      const dataToSend = { 
        ...settings,
        company_phone: settings.phone_country_code + ' ' + settings.company_phone.replace(/\s/g, ''),
        company_siret: settings.company_siret.replace(/\s/g, '')
      };
      if (!dataToSend.smtp_password) {
        delete dataToSend.smtp_password;
      }
      
      await axios.put(`${API}/settings`, dataToSend);
      
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.company_name = settings.company_name;
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success('Paramètres enregistrés');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPEG, PNG ou WEBP');
      return;
    }

    if (file.size > 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 1 Mo)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/settings/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setHasLogo(true);
      toast.success('Logo uploadé avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/settings/logo`);
      setHasLogo(false);
      toast.success('Logo supprimé');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-3 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="py-6 space-y-6 max-w-3xl"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
          <Gear size={20} className="text-slate-600 dark:text-slate-400" weight="duotone" />
        </div>
        <span className="text-lg font-semibold text-foreground">Paramètres</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
            activeTab === 'general' 
              ? 'bg-black dark:bg-white text-white dark:text-black' 
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <Buildings size={16} weight={activeTab === 'general' ? 'fill' : 'regular'} />
          Général
        </button>
        <button
          onClick={() => setActiveTab('chorus')}
          className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
            activeTab === 'chorus' 
              ? 'bg-black dark:bg-white text-white dark:text-black' 
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <FileText size={16} weight={activeTab === 'chorus' ? 'fill' : 'regular'} />
          Données Pro
        </button>
        <button
          onClick={() => setActiveTab('efacture')}
          className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
            activeTab === 'efacture' 
              ? 'bg-black dark:bg-white text-white dark:text-black' 
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <Shield size={16} weight={activeTab === 'efacture' ? 'fill' : 'regular'} />
          E-Facture
        </button>
        <button
          onClick={() => setActiveTab('rge')}
          className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
            activeTab === 'rge' 
              ? 'bg-black dark:bg-white text-white dark:text-black' 
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <Leaf size={16} weight={activeTab === 'rge' ? 'fill' : 'regular'} />
          RGE & TVA
        </button>
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <div className="space-y-4">
      {/* Email Configuration */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="premium-card p-6 ring-2 ring-amber-200 dark:ring-amber-900/50"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Envelope size={20} className="text-amber-600" weight="duotone" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-foreground">Configuration Email</h2>
          </div>
          {smtpConfigured ? (
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-2 py-1 rounded-full">
              <CheckCircle size={14} weight="fill" /> Configuré
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded-full">
              <WarningCircle size={14} weight="fill" /> Non configuré
            </span>
          )}
        </div>
        
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl mb-4">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            <strong>Important :</strong> Pour envoyer des devis et factures par email, 
            configurez votre email professionnel. Pour Gmail, utilisez un 
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline ml-1">
              mot de passe d'application
            </a>.
          </p>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Adresse email d'envoi</Label>
            <Input
              type="email"
              value={settings.smtp_email}
              onChange={(e) => setSettings({ ...settings, smtp_email: e.target.value })}
              className={`mt-1 rounded-xl ${validationErrors.smtp_email ? 'ring-2 ring-red-500' : ''}`}
              placeholder="votre-email@gmail.com"
            />
            {validationErrors.smtp_email && (
              <p className="text-xs text-red-500 mt-1">{validationErrors.smtp_email}</p>
            )}
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground">
              Mot de passe d'application
              {smtpConfigured && <span className="text-muted-foreground ml-2">(laissez vide pour garder l'actuel)</span>}
            </Label>
            <Input
              type="password"
              value={settings.smtp_password}
              onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
              className="mt-1 rounded-xl"
              placeholder={smtpConfigured ? "••••••••••••••••" : "Mot de passe d'application"}
            />
          </div>
        </div>
      </motion.div>

      {/* Monthly Objective */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="premium-card p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <Target size={20} className="text-emerald-600" weight="duotone" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-foreground">Objectif mensuel</h2>
            <p className="text-xs text-muted-foreground">Votre objectif de CA mensuel pour le tableau de bord</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Label className="text-sm font-medium text-muted-foreground">Objectif CA (€/mois)</Label>
            <div className="relative mt-1">
              <Input
                type="number"
                min="0"
                step="500"
                value={settings.monthly_objective}
                onChange={(e) => setSettings({ ...settings, monthly_objective: parseInt(e.target.value) || 0 })}
                className="rounded-xl pr-12"
                placeholder="10000"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-muted-foreground">
                <CurrencyEur size={16} />
              </div>
            </div>
          </div>
        </div>
        
        <p className="text-xs text-muted-foreground mt-3">
          Cet objectif est utilisé pour calculer le pourcentage affiché sur le graphique du tableau de bord
        </p>
      </motion.div>

      {/* Company Status & Location */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="premium-card p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
            <Shield size={20} className="text-violet-600" weight="duotone" />
          </div>
          <h2 className="font-semibold text-foreground">Statut juridique et localisation</h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Statut juridique de l'entreprise</Label>
            <select
              value={settings.company_status}
              onChange={(e) => setSettings({ ...settings, company_status: e.target.value })}
              className="mt-1 w-full h-12 px-4 border border-border rounded-xl bg-background text-foreground"
            >
              {Object.entries(statuses).map(([key, status]) => (
                <option key={key} value={key}>{status.name}</option>
              ))}
            </select>
            {statuses[settings.company_status] && (
              <p className="text-xs text-muted-foreground mt-1">{statuses[settings.company_status].description}</p>
            )}
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <MapPinArea size={16} />
              Localisation de l'activité
            </Label>
            <select
              value={settings.company_location}
              onChange={(e) => setSettings({ ...settings, company_location: e.target.value })}
              className="mt-1 w-full h-12 px-4 border border-border rounded-xl bg-background text-foreground"
            >
              {Object.entries(locations).map(([key, loc]) => (
                <option key={key} value={key}>{loc.name}</option>
              ))}
            </select>
            {locations[settings.company_location] && (
              <p className="text-xs text-muted-foreground mt-1">
                {locations[settings.company_location].tva_normal > 0 
                  ? `TVA applicable: ${locations[settings.company_location].tva_normal}%`
                  : 'Territoire exonéré de TVA'}
              </p>
            )}
          </div>

          {/* Rules Preview */}
          {rulesPreview && (
            <div className="p-4 bg-muted/50 rounded-2xl">
              <div className="flex items-center gap-2 mb-2">
                <Info size={16} className="text-amber-500" weight="fill" />
                <span className="text-sm font-semibold text-foreground">Règles appliquées</span>
              </div>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>
                  <strong className="text-foreground">TVA :</strong> {rulesPreview.tva_applicable 
                    ? `${rulesPreview.tva_rate}% applicable` 
                    : 'Non applicable'}
                </p>
                {rulesPreview.legal_mention && (
                  <p><strong className="text-foreground">Mention :</strong> <em>{rulesPreview.legal_mention}</em></p>
                )}
              </div>
            </div>
          )}

          <div>
            <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Briefcase size={16} />
              Domaine d'activité
            </Label>
            <select
              value={settings.job_domain}
              onChange={(e) => setSettings({ ...settings, job_domain: e.target.value })}
              className="mt-1 w-full h-12 px-4 border border-border rounded-xl bg-background text-foreground"
              data-testid="job-domain-select"
            >
              {Object.entries(jobDomains).map(([key, domain]) => (
                <option key={key} value={key}>{domain.name}</option>
              ))}
            </select>
            {jobDomains[settings.job_domain] && (
              <p className="text-xs text-muted-foreground mt-1">{jobDomains[settings.job_domain].description}</p>
            )}
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Percent size={16} />
              Pourcentage d'acompte
            </Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={settings.deposit_percentage}
              onChange={(e) => setSettings({ ...settings, deposit_percentage: parseInt(e.target.value) || 0 })}
              className="mt-1 rounded-xl"
              placeholder="30"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Pourcentage demandé à la commande (le solde sera facturé à la réception)
            </p>
          </div>
        </div>
      </motion.div>

      {/* Company Info */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="premium-card p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Buildings size={20} className="text-blue-600" weight="duotone" />
          </div>
          <h2 className="font-semibold text-foreground">Informations entreprise</h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Nom de l'entreprise</Label>
            <Input
              value={settings.company_name}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              className="mt-1 rounded-xl"
              placeholder="Ma Société SARL"
            />
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <MapPin size={16} />
              Adresse
            </Label>
            <Input
              value={settings.company_address}
              onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
              className="mt-1 rounded-xl"
              placeholder="123 Rue Example, 75001 Paris"
            />
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Phone size={16} />
              Téléphone
            </Label>
            <div className="flex gap-2 mt-1">
              <select
                value={settings.phone_country_code}
                onChange={(e) => setSettings({ ...settings, phone_country_code: e.target.value })}
                className="w-28 h-12 px-3 border border-border rounded-xl bg-background text-foreground text-sm"
              >
                {countryCodes.map((c, index) => (
                  <option key={`${c.code}-${index}`} value={c.code}>
                    {c.flag} {c.code}
                  </option>
                ))}
              </select>
              <Input
                value={settings.company_phone}
                onChange={(e) => setSettings({ ...settings, company_phone: formatPhone(e.target.value) })}
                className={`flex-1 rounded-xl ${validationErrors.company_phone ? 'ring-2 ring-red-500' : ''}`}
                placeholder="06 12 34 56 78"
              />
            </div>
            {validationErrors.company_phone && (
              <p className="text-xs text-red-500 mt-1">{validationErrors.company_phone}</p>
            )}
          </div>
        </div>
      </motion.div>

      {/* Legal Info */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="premium-card p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <CreditCard size={20} className="text-emerald-600" weight="duotone" />
          </div>
          <h2 className="font-semibold text-foreground">Informations légales</h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">SIRET (14 chiffres)</Label>
            <Input
              value={settings.company_siret}
              onChange={(e) => setSettings({ ...settings, company_siret: formatSiret(e.target.value) })}
              className={`mt-1 rounded-xl ${validationErrors.company_siret ? 'ring-2 ring-red-500' : ''}`}
              placeholder="123 456 789 00012"
              maxLength={17}
            />
            {validationErrors.company_siret && (
              <p className="text-xs text-red-500 mt-1">{validationErrors.company_siret}</p>
            )}
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground">Numéro de TVA intracommunautaire</Label>
            <Input
              value={settings.company_tva_number}
              onChange={(e) => setSettings({ ...settings, company_tva_number: e.target.value.toUpperCase() })}
              className={`mt-1 rounded-xl ${validationErrors.company_tva_number ? 'ring-2 ring-red-500' : ''}`}
              placeholder="FR 12 345678901"
              disabled={rulesPreview && !rulesPreview.tva_applicable}
            />
            {validationErrors.company_tva_number && (
              <p className="text-xs text-red-500 mt-1">{validationErrors.company_tva_number}</p>
            )}
            {rulesPreview && !rulesPreview.tva_applicable && (
              <p className="text-xs text-muted-foreground mt-1">Non applicable pour votre statut/localisation</p>
            )}
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground">Garantie décennale (N° police)</Label>
            <Input
              value={settings.company_garantie_decennale}
              onChange={(e) => setSettings({ ...settings, company_garantie_decennale: e.target.value })}
              className="mt-1 rounded-xl"
              placeholder="N° de police d'assurance"
            />
          </div>
        </div>
      </motion.div>

      {/* Logo */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="premium-card p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
            <Image size={20} className="text-pink-600" weight="duotone" />
          </div>
          <h2 className="font-semibold text-foreground">Logo de l'entreprise</h2>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">
          Votre logo apparaîtra sur vos devis et factures PDF. Format: PNG ou JPEG, max 1 Mo.
        </p>

        <div className="flex gap-3">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleLogoUpload}
            style={{ display: 'none' }}
            id="logo-upload"
          />
          <button
            onClick={() => document.getElementById('logo-upload').click()}
            disabled={uploading}
            className="flex-1 py-3 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <CloudArrowUp size={18} />
            {uploading ? 'Upload...' : (hasLogo ? 'Changer le logo' : 'Uploader un logo')}
          </button>
          {hasLogo && (
            <button onClick={handleDeleteLogo} className="w-12 h-12 rounded-xl bg-muted hover:bg-red-100 dark:hover:bg-red-900/30 flex items-center justify-center transition-colors">
              <Trash size={18} className="text-red-500" />
            </button>
          )}
        </div>

        {hasLogo && (
          <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl">
            <p className="text-sm text-emerald-700 dark:text-emerald-400 font-medium flex items-center gap-2">
              <CheckCircle size={18} weight="fill" /> Logo configuré
            </p>
          </div>
        )}
      </motion.div>
        </div>
      )}

      {/* Chorus Pro Tab */}
      {activeTab === 'chorus' && (
        <div className="space-y-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="premium-card p-6 ring-2 ring-violet-200 dark:ring-violet-900/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <FileText size={20} className="text-violet-600" weight="duotone" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-foreground">Facturation Électronique - Chorus Pro</h2>
              </div>
              {settings.chorus_pro_enabled ? (
                <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-2 py-1 rounded-full">
                  <CheckCircle size={14} weight="fill" /> Activé
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">
                  Non configuré
                </span>
              )}
            </div>
            
            <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-2xl mb-6">
              <p className="text-sm text-violet-800 dark:text-violet-300">
                <strong>Chorus Pro</strong> est la plateforme gratuite de facturation électronique pour le secteur public 
                et bientôt le B2B. L'inscription se fait sur <a href="https://portail.chorus-pro.gouv.fr" target="_blank" rel="noopener noreferrer" className="underline font-medium">portail.chorus-pro.gouv.fr</a>
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-foreground">Données entreprise obligatoires</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Code APE/NAF</Label>
                  <Input
                    value={settings.company_ape_code || ''}
                    onChange={(e) => setSettings({ ...settings, company_ape_code: e.target.value })}
                    placeholder="4321A"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">RCS ou N° RM</Label>
                  <Input
                    value={settings.company_rcs || ''}
                    onChange={(e) => setSettings({ ...settings, company_rcs: e.target.value })}
                    placeholder="RCS Paris B 123 456 789"
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Capital social (€)</Label>
                  <Input
                    type="number"
                    value={settings.company_capital || ''}
                    onChange={(e) => setSettings({ ...settings, company_capital: e.target.value })}
                    placeholder="10000"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Assurance décennale - Compagnie</Label>
                  <Input
                    value={settings.company_assurance_name || ''}
                    onChange={(e) => setSettings({ ...settings, company_assurance_name: e.target.value })}
                    placeholder="AXA, MAAF, etc."
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-muted-foreground">N° police assurance décennale</Label>
                <Input
                  value={settings.company_assurance_number || ''}
                  onChange={(e) => setSettings({ ...settings, company_assurance_number: e.target.value })}
                  placeholder="123456789"
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="premium-card p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <Shield size={20} className="text-muted-foreground" weight="duotone" />
              <h3 className="font-semibold text-foreground">Identifiants Chorus Pro (PISTE)</h3>
            </div>

            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl mb-4">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Pour obtenir vos identifiants techniques, créez un compte sur 
                <a href="https://developer.aife.economie.gouv.fr/" target="_blank" rel="noopener noreferrer" className="underline font-medium ml-1 inline-flex items-center gap-1">
                  developer.aife.economie.gouv.fr <ArrowSquareOut size={14} />
                </a>
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Identifiant technique PISTE</Label>
                <Input
                  value={settings.chorus_pro_tech_id || ''}
                  onChange={(e) => setSettings({ ...settings, chorus_pro_tech_id: e.target.value })}
                  placeholder="tech_xxxxxxxxxxxx"
                  className="mt-1 rounded-xl"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-muted-foreground">Identifiant structure Chorus Pro</Label>
                <Input
                  value={settings.chorus_pro_structure_id || ''}
                  onChange={(e) => setSettings({ ...settings, chorus_pro_structure_id: e.target.value })}
                  placeholder="struct_xxxxxxxxxxxx"
                  className="mt-1 rounded-xl"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-muted-foreground">Code service par défaut (optionnel)</Label>
                <Input
                  value={settings.chorus_pro_service_code || ''}
                  onChange={(e) => setSettings({ ...settings, chorus_pro_service_code: e.target.value })}
                  placeholder="SERVICE_COMPTA"
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* E-Facture Tab (Iopole) */}
      {activeTab === 'efacture' && (
        <div className="space-y-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="premium-card p-6 ring-2 ring-blue-200 dark:ring-blue-900/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Shield size={20} className="text-blue-600" weight="duotone" />
              </div>
              <h2 className="font-semibold text-foreground">Facturation Électronique - Iopole</h2>
            </div>
            
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl mb-6">
              <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                <strong>Obligatoire à partir du 1er septembre 2026</strong>
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-400">
                Iopole est une Plateforme de Dématérialisation Partenaire (PDP) agréée par la DGFiP. 
                Elle gère automatiquement la transmission de vos factures selon le type de client :
              </p>
              <ul className="text-sm text-blue-700 dark:text-blue-400 mt-2 space-y-1 ml-4">
                <li>• <strong>B2C (Particuliers)</strong> → E-reporting à la DGFiP</li>
                <li>• <strong>B2B (Entreprises)</strong> → E-invoicing direct au destinataire</li>
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl">
                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Particuliers (B2C)</p>
                <p className="text-lg font-bold text-emerald-800 dark:text-emerald-300">E-Reporting</p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400">Données transmises à la DGFiP</p>
              </div>
              <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-2xl">
                <p className="text-xs text-violet-600 dark:text-violet-400 font-medium">Entreprises (B2B)</p>
                <p className="text-lg font-bold text-violet-800 dark:text-violet-300">E-Invoicing</p>
                <p className="text-xs text-violet-600 dark:text-violet-400">Facture envoyée au client via Peppol</p>
              </div>
            </div>

            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl mb-4">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                <strong>Prérequis pour l'activation :</strong>
              </p>
              <ul className="text-sm text-amber-700 dark:text-amber-400 mt-1 space-y-1 ml-4">
                <li className="flex items-center gap-2">
                  {settings.company_siret ? <CheckCircle size={16} className="text-emerald-500" weight="fill" /> : <WarningCircle size={16} className="text-amber-500" weight="fill" />}
                  SIRET renseigné
                </li>
                <li className="flex items-center gap-2">
                  {settings.company_name ? <CheckCircle size={16} className="text-emerald-500" weight="fill" /> : <WarningCircle size={16} className="text-amber-500" weight="fill" />}
                  Raison sociale renseignée
                </li>
                <li className="flex items-center gap-2">
                  {settings.company_address ? <CheckCircle size={16} className="text-emerald-500" weight="fill" /> : <WarningCircle size={16} className="text-amber-500" weight="fill" />}
                  Adresse renseignée
                </li>
              </ul>
            </div>

            <div className="text-center">
              <a 
                href="https://www.iopole.fr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
              >
                Créer un compte Iopole <ArrowSquareOut size={16} />
              </a>
              <p className="text-xs text-muted-foreground mt-1">
                Plateforme gratuite pour les petits volumes
              </p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="premium-card p-6"
          >
            <h3 className="font-semibold text-foreground mb-4">Formats supportés</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-4 bg-muted/50 rounded-2xl text-center">
                <p className="font-semibold text-foreground">Factur-X</p>
                <p className="text-xs text-muted-foreground">PDF + XML</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-2xl text-center">
                <p className="font-semibold text-foreground">UBL 2.1</p>
                <p className="text-xs text-muted-foreground">Peppol BIS 3.0</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-2xl text-center">
                <p className="font-semibold text-foreground">CII</p>
                <p className="text-xs text-muted-foreground">Cross Industry</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-2xl text-center">
                <p className="font-semibold text-foreground">JSON</p>
                <p className="text-xs text-muted-foreground">API native</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* RGE & TVA Tab */}
      {activeTab === 'rge' && (
        <div className="space-y-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="premium-card p-6 ring-2 ring-emerald-200 dark:ring-emerald-900/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Leaf size={20} className="text-emerald-600" weight="duotone" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-foreground">Qualification RGE</h2>
              </div>
              {settings.company_rge_number ? (
                <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-2 py-1 rounded-full">
                  <CheckCircle size={14} weight="fill" /> Qualifié
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">
                  Non renseigné
                </span>
              )}
            </div>
            
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl mb-6">
              <p className="text-sm text-emerald-800 dark:text-emerald-300">
                <strong>RGE (Reconnu Garant de l'Environnement)</strong> est obligatoire pour faire bénéficier 
                vos clients de la TVA à 5.5% sur les travaux de rénovation énergétique et des aides 
                (MaPrimeRénov', CEE...).
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Numéro de qualification RGE</Label>
                <Input
                  value={settings.company_rge_number || ''}
                  onChange={(e) => setSettings({ ...settings, company_rge_number: e.target.value })}
                  placeholder="E-12345"
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="premium-card p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <Percent size={20} className="text-muted-foreground" weight="duotone" />
              <h3 className="font-semibold text-foreground">Taux de TVA applicables</h3>
            </div>

            <div className="space-y-3">
              <div className="p-4 bg-muted/50 rounded-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-foreground">TVA 20% - Taux normal</p>
                    <p className="text-sm text-muted-foreground">Construction neuve ou locaux de moins de 2 ans</p>
                  </div>
                  <span className="text-2xl font-bold text-muted-foreground">20%</span>
                </div>
              </div>

              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-amber-800 dark:text-amber-300">TVA 10% - Rénovation</p>
                    <p className="text-sm text-amber-700 dark:text-amber-400">Travaux de rénovation sur logement &gt; 2 ans</p>
                  </div>
                  <span className="text-2xl font-bold text-amber-500">10%</span>
                </div>
                <p className="text-xs text-amber-600 dark:text-amber-500 mt-2">Attestation simplifiée requise</p>
              </div>

              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-emerald-800 dark:text-emerald-300">TVA 5.5% - Rénovation énergétique</p>
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">Isolation, chauffage, énergies renouvelables...</p>
                  </div>
                  <span className="text-2xl font-bold text-emerald-500">5.5%</span>
                </div>
                <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-2">
                  {settings.company_rge_number 
                    ? '✓ Qualification RGE validée - Vous pouvez appliquer ce taux'
                    : '⚠️ Nécessite une qualification RGE'}
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>Travaux éligibles à la TVA 5.5% :</strong><br/>
                • Isolation thermique (parois opaques et vitrées)<br/>
                • Chauffage, ventilation, régulation thermique<br/>
                • Production d'eau chaude sanitaire<br/>
                • Énergies renouvelables (hors PAC air-air)<br/>
                • Raccordement réseau de chaleur
              </p>
            </div>
          </motion.div>
        </div>
      )}

      {/* Save Button */}
      <motion.button 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        onClick={handleSave} 
        disabled={saving} 
        className="w-full py-4 rounded-2xl bg-black dark:bg-white text-white dark:text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-all disabled:opacity-50"
      >
        <FloppyDisk size={20} weight="bold" />
        {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
      </motion.button>
    </motion.div>
  );
};

export default Settings;
